import React, { useRef } from "react";
import ConsumptionGraph from "./ConsumptionGraph";
import teaCoffeeBackground from "../../Assets/icons/coffee-hand.png";
import MascotLoadingOverlay from "../shared/MascotLoadingOverlay";

function ConsumptionGraphSection({
  dashboardConsumentIsLoading,
  dashboardConsumentIsFetching,
  dashboardType,
  warn,
  monthlyConsumption,
}) {
  const hasLoadedOnce = useRef(false);
  if (monthlyConsumption && monthlyConsumption.length > 0 && !dashboardConsumentIsLoading && !dashboardConsumentIsFetching) {
    hasLoadedOnce.current = true;
  }
  const isLoading = dashboardConsumentIsLoading || dashboardConsumentIsFetching;
  const showSkeleton = isLoading || (!hasLoadedOnce.current && (!monthlyConsumption || monthlyConsumption.length === 0));

  return (
    <div className="w-full lg:w-[60%] h-[420px] sm:h-[450px] md:h-[475px] lg:h-auto relative bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 flex flex-col">
      {/* Header — diğer sayfalardaki PageHeader diliyle aynı */}
      <div className="bg-gray-50 p-3 sm:p-4 border-b border-gray-200 shrink-0">
        <div className="flex items-center">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg sm:rounded-xl shadow-sm border border-blue-100">
              <svg
                className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-base sm:text-xl font-bold text-gray-900">
                Son 6 Ay Tüketimi
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                Aylık tüketim trend analizi
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Graph Content */}
      <div className="relative flex-1 min-h-0 p-2 sm:p-4">
        {/* Background Image */}
        <div className="absolute right-0 bottom-0 pointer-events-none opacity-5">
          <img
            src={teaCoffeeBackground}
            alt="background"
            className="w-48 h-48 sm:w-72 sm:h-72 object-cover"
          />
        </div>

        {/* Graph or Skeleton Loading */}
        <div className="h-full relative z-10">
          {showSkeleton ? (
            <div className="w-full h-full relative">
              <div className="w-full h-full flex flex-col items-center justify-center opacity-40">
                {/* Skeleton Chart */}
                <div className="w-full h-full flex flex-col">
                  {/* Legend Skeleton - Top */}
                  <div className="flex flex-wrap gap-4 mb-4 justify-center">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>

                  {/* Chart Area Skeleton */}
                  <div className="flex-1 relative">
                    {/* Y-Axis Skeleton */}
                    <div className="absolute left-0 top-0 bottom-0 w-8 flex flex-col justify-between py-4">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div
                          key={i}
                          className="h-4 w-12 bg-gray-200 rounded animate-pulse"
                        />
                      ))}
                    </div>

                    {/* Chart Bars Skeleton */}
                    <div className="ml-8 mr-4 h-full flex items-end justify-between gap-2">
                      {Array.from({ length: 4 }).map((_, monthIndex) => (
                        <div
                          key={monthIndex}
                          className="flex-1 flex flex-col items-center gap-1"
                        >
                          {/* Month Label Skeleton */}
                          <div className="h-4 w-16 bg-gray-200 rounded animate-pulse mb-2" />

                          {/* Bars Skeleton */}
                          <div className="flex flex-col gap-1 w-full">
                            {Array.from({ length: 3 }).map((_, barIndex) => (
                              <div
                                key={barIndex}
                                className="w-full bg-gray-200 rounded-t animate-pulse"
                                style={{
                                  height: `${Math.random() * 60 + 20}%`,
                                  animationDelay: `${barIndex * 0.1}s`,
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* X-Axis Line Skeleton */}
                    <div className="absolute bottom-0 left-8 right-4 h-px bg-gray-200" />
                  </div>
                </div>
              </div>
              <MascotLoadingOverlay message="Son 6 Ay Tüketim Grafiği Yükleniyor..." />
            </div>
          ) : (
            <ConsumptionGraph
              dashboardConsumentIsLoading={dashboardConsumentIsLoading}
              dashboardConsumentIsFetching={dashboardConsumentIsFetching}
              dashboardType={dashboardType}
              warn={warn}
              monthlyConsumption={monthlyConsumption}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(ConsumptionGraphSection);
